import * as yup from 'yup';
import { DateTime } from 'luxon';
import { CitizenshipFormVM } from 'mutation/view-models/registration-citizenship';
import { AccordionEditDataControl } from 'shell/view-models/edit-data-control';
import { idActRegex, regVolRegex } from 'shell/constants/regex';
import { ActType } from 'shell/constants/act-type';
import { StateRegistrationCitizenship } from './state-registration-citizenship';
import { DocumentDigital } from 'shell/view-models/act-form';

export const validationSchema = yup.object().shape({
  editDataControl: yup
    .object()
    .nullable()
    .shape({
      dateValidity: yup
        .date()
        .nullable()
        .required('Campo obbligatorio')
        .typeError('Data non valida'),
    })
    // error dateValidity
    .test(
      'editDataControl.dateValidity',
      'La data deve essere minore uguale di domani',
      (values) => {
        const castedValues = values as unknown as AccordionEditDataControl;

        if (
          castedValues?.dateValidity &&
          castedValues?.dateValidity > DateTime.now().plus({ day: 1 })
        ) {
          return new yup.ValidationError(
            'La data deve essere minore uguale di domani',
            undefined,
            'editDataControl.dateValidity'
          );
        }

        return true;
      }
    ),
  newCitizenship: yup
    .object()
    .nullable()
    .shape({
      reasonCitizenship: yup.string().nullable().required('Campo obbligatorio'),
      toDPR: yup
        .object()
        .nullable()
        .when('reasonCitizenship', {
          is: (reasonCitizenship: string) =>
            reasonCitizenship && reasonCitizenship === StateRegistrationCitizenship.ToDPR,
          then: yup
            .object()
            .nullable()
            .shape({
              dateBuying: yup
                .date()
                .nullable()
                .notRequired()
                .typeError('Data non valida')
                .max(
                  DateTime.now().plus({ day: 1 }),
                  'La data decorrenza non può essere superiore al giorno successivo alla data di dichiarazione'
                )
                .when('dateDPR', (dateDPR, formvalidationSchema) =>
                  dateDPR && dateDPR?.toString() !== 'Invalid Date'
                    ? formvalidationSchema.min(
                        dateDPR,
                        'La data deve essere maggiore della data di DM-DPR-DP'
                      )
                    : formvalidationSchema.nullable()
                ),
              dateDPR: yup
                .date()
                .nullable()
                .notRequired()
                .typeError('Data non valida')
                .max(DateTime.now(), 'La data deve essere minore del giorno corrente'),
              dateOath: yup
                .date()
                .nullable()
                .notRequired()
                .typeError('Data non valida')
                .max(DateTime.now(), 'La data deve essere minore del giorno corrente')
                .when('dateBuying', (dateBuying, formvalidationSchema) =>
                  dateBuying && dateBuying?.toString() !== 'Invalid Date'
                    ? formvalidationSchema.max(
                        dateBuying,
                        'La data deve essere minore della data di acquisto cittadinanza'
                      )
                    : formvalidationSchema.nullable()
                ),
              actType: yup.string(),
              digital: yup
                .object()
                .nullable()
                .when('actType', {
                  is: ActType.AttoDigitale,
                  otherwise: yup.object().nullable(),
                  then: yup
                    .object()
                    .nullable()
                    .shape({
                      idAnsc: yup
                        .string()
                        .nullable()
                        .matches(idActRegex, 'Formato non corretto')
                        .test((value) => {
                          if (value) {
                            const [year] = value.split('-');
                            const isValid = year ? Number.parseInt(year) >= 2022 : true;

                            return isValid
                              ? true
                              : new yup.ValidationError(
                                  "L'idAnsc deve avere anno maggiore al 2022",
                                  undefined,
                                  'newCitizenship.toDPR.digital.idAnsc'
                                );
                          }
                          return true;
                        })
                        .required('Campo richiesto'),
                      documentDate: yup
                        .date()
                        .nullable()
                        .notRequired()
                        .typeError('Formato non valido'),
                      municipality: yup.object().shape({}).nullable().required('Campo richiesto'),
                    })
                    .test((values) => {
                      const castedValues = values as unknown as DocumentDigital;

                      if (
                        castedValues?.municipality?.id &&
                        castedValues?.istatCode &&
                        castedValues?.municipality?.id === castedValues.istatCode
                      ) {
                        return true;
                      }
                      return new yup.ValidationError([
                        new yup.ValidationError(
                          'Non coincidono i due codici ISTAT',
                          undefined,
                          'newCitizenship.toDPR.digital.municipality'
                        ),
                        new yup.ValidationError(
                          'Non coincidono i due codici ISTAT',
                          undefined,
                          'newCitizenship.toDPR.digital.istatCode'
                        ),
                      ]);
                    }),
                }),
              paper: yup
                .object()
                .nullable()
                .when('actType', {
                  is: ActType.AttoCartaceo,
                  otherwise: yup.object().nullable(),
                  then: yup.object().shape({
                    actDate: yup
                      .date()
                      .nullable()
                      .max(
                        DateTime.local().plus({ days: 1 }),
                        'La data non può essere successiva alla data odierna'
                      )
                      .typeError('Data non valida'),
                    transcribed: yup.string().nullable(),
                    actMunicipality: yup
                      .object()
                      .nullable()
                      .shape({})
                      .required('Il campo è richiesto'),
                    actYear: yup
                      .number()
                      .nullable()
                      .required('Campo richiesto')
                      .typeError('Sono ammesse solamente cifre'),
                    actNumber: yup
                      .number()
                      .nullable()
                      .required('Campo richiesto')
                      .typeError('Sono ammesse solamente cifre'),
                    actPart: yup
                      .string()
                      .nullable()
                      .required('Campo richiesto')
                      .test(
                        'part-ammisible-values',
                        'Gli unici valori ammessi sono 1 o 2',
                        (value) => ['1', '2'].indexOf(value || '') >= 0
                      ),
                    actSeries: yup.string().nullable(),
                    actVolume: yup
                      .string()
                      .nullable()
                      .when('actMunicipality.description', {
                        is: (description: string) =>
                          (description || '').toLocaleLowerCase() === 'milano',
                        then: yup
                          .string()
                          // .required('Campo obbligatorio')
                          .matches(regVolRegex, {
                            message:
                              'Per il comune di Milano il volume è formato da R e due cifre(es. R00)',
                          }),
                      }),
                  }),
                }),
            }),
          otherwise: yup.object().nullable(),
        }),
      choiceItalian: yup
        .object()
        .nullable()
        .when('reasonCitizenship', {
          is: (reasonCitizenship: string) =>
            reasonCitizenship && reasonCitizenship === StateRegistrationCitizenship.ChoiceItalian,
          then: yup
            .object()
            .nullable()
            .shape({
              dateBuying: yup
                .date()
                .nullable()
                .typeError('Data non valida')
                .required('Campo richiesto')
                .max(
                  DateTime.now().plus({ day: 1 }),
                  'La data decorrenza non può essere superiore al giorno successivo alla data di dichiarazione'
                ),
              dateOath: yup
                .date()
                .nullable()
                .required('Campo richiesto')
                .typeError('Data non valida')
                .max(DateTime.now(), 'La data deve essere minore del giorno corrente')
                .when('dateBuying', (dateBuying, formvalidationSchema) =>
                  dateBuying && dateBuying?.toString() !== 'Invalid Date'
                    ? formvalidationSchema.max(
                        dateBuying,
                        'La data deve essere minore della data di acquisto cittadinanza'
                      )
                    : formvalidationSchema.nullable()
                ),
              isForeign: yup.boolean(),
              italian: yup
                .object()
                .nullable()
                .when('isForeign', {
                  is: true,
                  otherwise: yup.object().nullable(),
                  then: yup.object().shape({}).required('Campo richiesto'),
                }),
              foreign: yup
                .object()
                .nullable()
                .when('isForeign', {
                  is: false,
                  otherwise: yup.object().nullable(),
                  then: yup.object().shape({}).required('Campo richiesto'),
                }),
              actType: yup.string(),
              digital: yup
                .object()
                .nullable()
                .when('actType', {
                  is: ActType.AttoDigitale,
                  otherwise: yup.object().nullable(),
                  then: yup
                    .object()
                    .nullable()
                    .shape({
                      idAnsc: yup
                        .string()
                        .nullable()
                        .matches(idActRegex, 'Formato non corretto')
                        .test((value) => {
                          if (value) {
                            const [year] = value.split('-');
                            const isValid = year ? Number.parseInt(year) >= 2022 : true;

                            return isValid
                              ? true
                              : new yup.ValidationError(
                                  "L'idAnsc deve avere anno maggiore al 2022",
                                  undefined,
                                  'newCitizenship.choiceItalian.digital.idAnsc'
                                );
                          }
                          return true;
                        })
                        .required('Campo richiesto'),
                      documentDate: yup
                        .date()
                        .nullable()
                        .notRequired()
                        .typeError('Formato non valido'),
                      municipality: yup.object().shape({}).nullable().required('Campo richiesto'),
                    })
                    .test((values) => {
                      const castedValues = values as unknown as DocumentDigital;

                      if (
                        castedValues?.municipality?.id &&
                        castedValues?.istatCode &&
                        castedValues?.municipality?.id === castedValues.istatCode
                      ) {
                        return true;
                      }
                      return new yup.ValidationError([
                        new yup.ValidationError(
                          'Non coincidono i due codici ISTAT',
                          undefined,
                          'newCitizenship.choiceItalian.digital.municipality'
                        ),
                        new yup.ValidationError(
                          'Non coincidono i due codici ISTAT',
                          undefined,
                          'newCitizenship.choiceItalian.digital.istatCode'
                        ),
                      ]);
                    }),
                }),
              paper: yup
                .object()
                .nullable()
                .when('actType', {
                  is: ActType.AttoCartaceo,
                  otherwise: yup.object().nullable(),
                  then: yup.object().shape({
                    actDate: yup
                      .date()
                      .nullable()
                      .max(
                        DateTime.local().plus({ days: 1 }),
                        'La data non può essere successiva alla data odierna'
                      )
                      .typeError('Data non valida'),
                    transcribed: yup.string().nullable(),
                    actMunicipality: yup
                      .object()
                      .nullable()
                      .shape({})
                      .required('Il campo è richiesto'),
                    actYear: yup
                      .number()
                      .nullable()
                      .required('Campo richiesto')
                      .typeError('Sono ammesse solamente cifre'),
                    actNumber: yup
                      .number()
                      .nullable()
                      .required('Campo richiesto')
                      .typeError('Sono ammesse solamente cifre'),
                    actPart: yup
                      .string()
                      .nullable()
                      .required('Campo richiesto')
                      .test(
                        'part-ammisible-values',
                        'Gli unici valori ammessi sono 1 o 2',
                        (value) => ['1', '2'].indexOf(value || '') >= 0
                      ),
                    actSeries: yup.string().nullable(),
                    actVolume: yup
                      .string()
                      .nullable()
                      .when('actMunicipality.description', {
                        is: (description: string) =>
                          (description || '').toLocaleLowerCase() === 'milano',
                        then: yup
                          .string()
                          // .required('Campo obbligatorio')
                          .matches(regVolRegex, {
                            message:
                              'Per il comune di Milano il volume è formato da R e due cifre(es. R00)',
                          }),
                      }),
                  }),
                }),
            }),
          otherwise: yup.object().nullable(),
        }),

      foreignCitizenship: yup
        .object()
        .nullable()
        .when('reasonCitizenship', {
          is: (reasonCitizenship: string) =>
            reasonCitizenship &&
            reasonCitizenship === StateRegistrationCitizenship.ForeignCitizenship,
          then: yup
            .object()
            .nullable()
            .shape({
              dateBuying: yup
                .date()
                .typeError('Data non valida')
                .nullable()
                .notRequired()
                .max(
                  DateTime.now().plus({ day: 1 }),
                  'La data decorrenza non può essere superiore al giorno successivo alla data di dichiarazione'
                ),
              dateOath: yup
                .date()
                .typeError('Data non valida')
                .nullable()
                .notRequired()
                .max(DateTime.now(), 'La data deve essere minore del giorno corrente'),
              actType: yup.string(),
              digital: yup
                .object()
                .nullable()
                .when('actType', {
                  is: ActType.AttoDigitale,
                  otherwise: yup.object().nullable(),
                  then: yup
                    .object()
                    .nullable()
                    .shape({
                      idAnsc: yup
                        .string()
                        .nullable()
                        .matches(idActRegex, 'Formato non corretto')
                        .test((value) => {
                          if (value) {
                            const [year] = value.split('-');
                            const isValid = year ? Number.parseInt(year) >= 2022 : true;

                            return isValid
                              ? true
                              : new yup.ValidationError(
                                  "L'idAnsc deve avere anno maggiore al 2022",
                                  undefined,
                                  'newCitizenship.foreignCitizenship.digital.idAnsc'
                                );
                          }
                          return true;
                        })
                        .required('Campo richiesto'),
                      documentDate: yup
                        .date()
                        .nullable()
                        .notRequired()
                        .typeError('Formato non valido'),
                      municipality: yup.object().shape({}).nullable().required('Campo richiesto'),
                    })
                    .test((values) => {
                      const castedValues = values as unknown as DocumentDigital;

                      if (
                        castedValues?.municipality?.id &&
                        castedValues?.istatCode &&
                        castedValues?.municipality?.id === castedValues.istatCode
                      ) {
                        return true;
                      }
                      return new yup.ValidationError([
                        new yup.ValidationError(
                          'Non coincidono i due codici ISTAT',
                          undefined,
                          'newCitizenship.foreignCitizenship.digital.municipality'
                        ),
                        new yup.ValidationError(
                          'Non coincidono i due codici ISTAT',
                          undefined,
                          'newCitizenship.foreignCitizenship.digital.istatCode'
                        ),
                      ]);
                    }),
                }),
              paper: yup
                .object()
                .nullable()
                .when('actType', {
                  is: ActType.AttoCartaceo,
                  otherwise: yup.object().nullable(),
                  then: yup.object().shape({
                    actDate: yup
                      .date()
                      .nullable()
                      .max(
                        DateTime.local().plus({ days: 1 }),
                        'La data non può essere successiva alla data odierna'
                      )
                      .typeError('Data non valida'),
                    transcribed: yup.string().nullable(),
                    actMunicipality: yup
                      .object()
                      .nullable()
                      .shape({})
                      .required('Il campo è richiesto'),
                    actYear: yup
                      .number()
                      .nullable()
                      .required('Campo richiesto')
                      .typeError('Sono ammesse solamente cifre'),
                    actNumber: yup
                      .number()
                      .nullable()
                      .required('Campo richiesto')
                      .typeError('Sono ammesse solamente cifre'),
                    actPart: yup
                      .string()
                      .nullable()
                      .required('Campo richiesto')
                      .test(
                        'part-ammisible-values',
                        'Gli unici valori ammessi sono 1 o 2',
                        (value) => ['1', '2'].indexOf(value || '') >= 0
                      ),
                    actSeries: yup.string().nullable(),
                    actVolume: yup
                      .string()
                      .nullable()
                      .when('actMunicipality.description', {
                        is: (description: string) =>
                          (description || '').toLocaleLowerCase() === 'milano',
                        then: yup
                          .string()
                          // .required('Campo obbligatorio')
                          .matches(regVolRegex, {
                            message:
                              'Per il comune di Milano il volume è formato da R e due cifre(es. R00)',
                          }),
                      }),
                  }),
                }),
            }),
          otherwise: yup.object().nullable(),
        }),

      foreignNaturalization: yup
        .object()
        .nullable()
        .when('reasonCitizenship', {
          is: (reasonCitizenship: string) =>
            reasonCitizenship &&
            reasonCitizenship === StateRegistrationCitizenship.ForeignNaturalization,
          then: yup
            .object()
            .nullable()
            .shape({
              dateBuying: yup
                .date()
                .nullable()
                .typeError('Data non valida')
                .notRequired()
                .max(
                  DateTime.now().plus({ day: 1 }),
                  'La data decorrenza non può essere superiore al giorno successivo alla data di dichiarazione'
                ),
              dateOath: yup
                .date()
                .nullable()
                .typeError('Data non valida')
                .notRequired()
                .max(DateTime.now(), 'La data deve essere minore del giorno corrente'),
              actType: yup.string(),
              digital: yup
                .object()
                .nullable()
                .when('actType', {
                  is: ActType.AttoDigitale,
                  otherwise: yup.object().nullable(),
                  then: yup
                    .object()
                    .nullable()
                    .shape({
                      idAnsc: yup
                        .string()
                        .nullable()
                        .matches(idActRegex, 'Formato non corretto')
                        .test((value) => {
                          if (value) {
                            const [year] = value.split('-');
                            const isValid = year ? Number.parseInt(year) >= 2022 : true;

                            return isValid
                              ? true
                              : new yup.ValidationError(
                                  "L'idAnsc deve avere anno maggiore al 2022",
                                  undefined,
                                  'newCitizenship.foreignNaturalization.digital.idAnsc'
                                );
                          }
                          return true;
                        })
                        .required('Campo richiesto'),
                      documentDate: yup
                        .date()
                        .nullable()
                        .notRequired()
                        .typeError('Formato non valido'),
                      municipality: yup.object().shape({}).nullable().required('Campo richiesto'),
                    })
                    .test((values) => {
                      const castedValues = values as unknown as DocumentDigital;

                      if (
                        castedValues?.municipality?.id &&
                        castedValues?.istatCode &&
                        castedValues?.municipality?.id === castedValues.istatCode
                      ) {
                        return true;
                      }
                      return new yup.ValidationError([
                        new yup.ValidationError(
                          'Non coincidono i due codici ISTAT',
                          undefined,
                          'newCitizenship.foreignNaturalization.digital.municipality'
                        ),
                        new yup.ValidationError(
                          'Non coincidono i due codici ISTAT',
                          undefined,
                          'newCitizenship.foreignNaturalization.digital.istatCode'
                        ),
                      ]);
                    }),
                }),
              paper: yup
                .object()
                .nullable()
                .when('actType', {
                  is: ActType.AttoCartaceo,
                  otherwise: yup.object().nullable(),
                  then: yup.object().shape({
                    actDate: yup
                      .date()
                      .nullable()
                      .max(
                        DateTime.local().plus({ days: 1 }),
                        'La data non può essere successiva alla data odierna'
                      )
                      .typeError('Data non valida'),
                    transcribed: yup.string().nullable(),
                    actMunicipality: yup
                      .object()
                      .nullable()
                      .shape({})
                      .required('Il campo è richiesto'),
                    actYear: yup
                      .number()
                      .nullable()
                      .required('Campo richiesto')
                      .typeError('Sono ammesse solamente cifre'),
                    actNumber: yup
                      .number()
                      .nullable()
                      .required('Campo richiesto')
                      .typeError('Sono ammesse solamente cifre'),
                    actPart: yup
                      .string()
                      .nullable()
                      .required('Campo richiesto')
                      .test(
                        'part-ammisible-values',
                        'Gli unici valori ammessi sono 1 o 2',
                        (value) => ['1', '2'].indexOf(value || '') >= 0
                      ),
                    actSeries: yup.string().nullable(),
                    actVolume: yup
                      .string()
                      .nullable()
                      .when('actMunicipality.description', {
                        is: (description: string) =>
                          (description || '').toLocaleLowerCase() === 'milano',
                        then: yup
                          .string()
                          // .required('Campo obbligatorio')
                          .matches(regVolRegex, {
                            message:
                              'Per il comune di Milano il volume è formato da R e due cifre(es. R00)',
                          }),
                      }),
                  }),
                }),
            }),
          otherwise: yup.object().nullable(),
        }),

      renunciationCitizenship: yup
        .object()
        .nullable()
        .when('reasonCitizenship', {
          is: (reasonCitizenship: string) =>
            reasonCitizenship &&
            reasonCitizenship === StateRegistrationCitizenship.RenunciationCitizenship,
          then: yup
            .object()
            .nullable()
            .shape({
              actType: yup.string(),
              digital: yup
                .object()
                .nullable()
                .when('actType', {
                  is: ActType.AttoDigitale,
                  otherwise: yup.object().nullable(),
                  then: yup
                    .object()
                    .nullable()
                    .shape({
                      idAnsc: yup
                        .string()
                        .nullable()
                        .matches(idActRegex, 'Formato non corretto')
                        .test((value) => {
                          if (value) {
                            const [year] = value.split('-');
                            const isValid = year ? Number.parseInt(year) >= 2022 : true;

                            return isValid
                              ? true
                              : new yup.ValidationError(
                                  "L'idAnsc deve avere anno maggiore al 2022",
                                  undefined,
                                  'newCitizenship.renunciationCitizenship.digital.idAnsc'
                                );
                          }
                          return true;
                        })
                        .required('Campo richiesto'),
                      documentDate: yup
                        .date()
                        .nullable()
                        .notRequired()
                        .typeError('Formato non valido'),
                      municipality: yup.object().shape({}).nullable().required('Campo richiesto'),
                    })
                    .test((values) => {
                      const castedValues = values as unknown as DocumentDigital;

                      if (
                        castedValues?.municipality?.id &&
                        castedValues?.istatCode &&
                        castedValues?.municipality?.id === castedValues.istatCode
                      ) {
                        return true;
                      }
                      return new yup.ValidationError([
                        new yup.ValidationError(
                          'Non coincidono i due codici ISTAT',
                          undefined,
                          'newCitizenship.renunciationCitizenship.digital.municipality'
                        ),
                        new yup.ValidationError(
                          'Non coincidono i due codici ISTAT',
                          undefined,
                          'newCitizenship.renunciationCitizenship.digital.istatCode'
                        ),
                      ]);
                    }),
                }),
              paper: yup
                .object()
                .nullable()
                .when('actType', {
                  is: ActType.AttoCartaceo,
                  otherwise: yup.object().nullable(),
                  then: yup.object().shape({
                    actDate: yup
                      .date()
                      .nullable()
                      .max(
                        DateTime.local().plus({ days: 1 }),
                        'La data non può essere successiva alla data odierna'
                      )
                      .typeError('Data non valida'),
                    transcribed: yup.string().nullable(),
                    actMunicipality: yup
                      .object()
                      .nullable()
                      .shape({})
                      .required('Il campo è richiesto'),
                    actYear: yup
                      .number()
                      .nullable()
                      .required('Campo richiesto')
                      .typeError('Sono ammesse solamente cifre'),
                    actNumber: yup
                      .number()
                      .nullable()
                      .required('Campo richiesto')
                      .typeError('Sono ammesse solamente cifre'),
                    actPart: yup
                      .string()
                      .nullable()
                      .required('Campo richiesto')
                      .test(
                        'part-ammisible-values',
                        'Gli unici valori ammessi sono 1 o 2',
                        (value) => ['1', '2'].indexOf(value || '') >= 0
                      ),
                    actSeries: yup.string().nullable(),
                    actVolume: yup
                      .string()
                      .nullable()
                      .when('actMunicipality.description', {
                        is: (description: string) =>
                          (description || '').toLocaleLowerCase() === 'milano',
                        then: yup
                          .string()
                          // .required('Campo obbligatorio')
                          .matches(regVolRegex, {
                            message:
                              'Per il comune di Milano il volume è formato da R e due cifre(es. R00)',
                          }),
                      }),
                  }),
                }),
            }),
          otherwise: yup.object().nullable(),
        }),

      italianCitizenshipDP: yup
        .object()
        .nullable()
        .when('reasonCitizenship', {
          is: (reasonCitizenship: string) =>
            reasonCitizenship &&
            reasonCitizenship === StateRegistrationCitizenship.ItalianCitizenshipDP,
          then: yup
            .object()
            .nullable()
            .shape({
              actType: yup.string(),
              digital: yup
                .object()
                .nullable()
                .when('actType', {
                  is: ActType.AttoDigitale,
                  otherwise: yup.object().nullable(),
                  then: yup
                    .object()
                    .nullable()
                    .shape({
                      idAnsc: yup
                        .string()
                        .nullable()
                        .matches(idActRegex, 'Formato non corretto')
                        .test((value) => {
                          if (value) {
                            const [year] = value.split('-');
                            const isValid = year ? Number.parseInt(year) >= 2022 : true;

                            return isValid
                              ? true
                              : new yup.ValidationError(
                                  "L'idAnsc deve avere anno maggiore al 2022",
                                  undefined,
                                  'newCitizenship.italianCitizenshipDP.digital.idAnsc'
                                );
                          }
                          return true;
                        })
                        .required('Campo richiesto'),
                      documentDate: yup
                        .date()
                        .nullable()
                        .notRequired()
                        .typeError('Formato non valido'),
                      municipality: yup.object().shape({}).nullable().required('Campo richiesto'),
                    })
                    .test((values) => {
                      const castedValues = values as unknown as DocumentDigital;

                      if (
                        castedValues?.municipality?.id &&
                        castedValues?.istatCode &&
                        castedValues?.municipality?.id === castedValues.istatCode
                      ) {
                        return true;
                      }
                      return new yup.ValidationError([
                        new yup.ValidationError(
                          'Non coincidono i due codici ISTAT',
                          undefined,
                          'newCitizenship.italianCitizenshipDP.digital.municipality'
                        ),
                        new yup.ValidationError(
                          'Non coincidono i due codici ISTAT',
                          undefined,
                          'newCitizenship.italianCitizenshipDP.digital.istatCode'
                        ),
                      ]);
                    }),
                }),
              paper: yup
                .object()
                .nullable()
                .when('actType', {
                  is: ActType.AttoCartaceo,
                  otherwise: yup.object().nullable(),
                  then: yup.object().shape({
                    actDate: yup
                      .date()
                      .nullable()
                      .max(
                        DateTime.local().plus({ days: 1 }),
                        'La data non può essere successiva alla data odierna'
                      )
                      .typeError('Data non valida'),
                    transcribed: yup.string().nullable(),
                    actMunicipality: yup
                      .object()
                      .nullable()
                      .shape({})
                      .required('Il campo è richiesto'),
                    actYear: yup
                      .number()
                      .nullable()
                      .required('Campo richiesto')
                      .typeError('Sono ammesse solamente cifre'),
                    actNumber: yup
                      .number()
                      .nullable()
                      .required('Campo richiesto')
                      .typeError('Sono ammesse solamente cifre'),
                    actPart: yup
                      .string()
                      .nullable()
                      .required('Campo richiesto')
                      .test(
                        'part-ammisible-values',
                        'Gli unici valori ammessi sono 1 o 2',
                        (value) => ['1', '2'].indexOf(value || '') >= 0
                      ),
                    actSeries: yup.string().nullable(),
                    actVolume: yup
                      .string()
                      .nullable()
                      .when('actMunicipality.description', {
                        is: (description: string) =>
                          (description || '').toLocaleLowerCase() === 'milano',
                        then: yup
                          .string()
                          // .required('Campo obbligatorio')
                          .matches(regVolRegex, {
                            message:
                              'Per il comune di Milano il volume è formato da R e due cifre(es. R00)',
                          }),
                      }),
                  }),
                }),
            }),
          otherwise: yup.object().nullable(),
        }),
    })
    // errors toDPR start
    .test('newCitizenship.toDPR.act.year', 'Campo obbligatorio', (values) => {
      const castedValues = values as unknown as CitizenshipFormVM;

      const errors: Array<yup.ValidationError> = [];

      if (
        castedValues.reasonCitizenship === StateRegistrationCitizenship.ToDPR &&
        castedValues.toDPR?.actType === ActType.AttoCartaceo &&
        !castedValues.toDPR?.paper?.actYear
      ) {
        errors.push(
          new yup.ValidationError(
            'Campo obbligatorio',
            undefined,
            'newCitizenship.toDPR.paper.actYear'
          )
        );
      }

      if (
        castedValues.reasonCitizenship === StateRegistrationCitizenship.ToDPR &&
        castedValues.toDPR?.actType === ActType.AttoCartaceo &&
        !castedValues.toDPR?.paper?.actNumber
      ) {
        errors.push(
          new yup.ValidationError(
            'Campo obbligatorio',
            undefined,
            'newCitizenship.toDPR.paper.actNumber'
          )
        );
      }

      if (
        castedValues.reasonCitizenship === StateRegistrationCitizenship.ToDPR &&
        castedValues.toDPR?.actType === ActType.AttoCartaceo &&
        !castedValues.toDPR?.paper?.actVolume
      ) {
        errors.push(
          new yup.ValidationError(
            'Campo obbligatorio',
            undefined,
            'newCitizenship.toDPR.paper.actVolume'
          )
        );
      }

      if (errors?.length === 0) {
        return true;
      }

      return new yup.ValidationError(errors);
    })
    // errors toDPR end
    // errors choiceItalian start
    .test('newCitizenship.choiceItalian.act.year', 'Campo obbligatorio', (values) => {
      const castedValues = values as unknown as CitizenshipFormVM;

      const errors: Array<yup.ValidationError> = [];

      if (
        castedValues.reasonCitizenship === StateRegistrationCitizenship.ChoiceItalian &&
        castedValues.choiceItalian?.actType === ActType.AttoCartaceo &&
        !castedValues.choiceItalian?.paper?.actYear
      ) {
        errors.push(
          new yup.ValidationError(
            'Campo obbligatorio',
            undefined,
            'newCitizenship.choiceItalian.paper.actYear'
          )
        );
      }

      if (
        castedValues.reasonCitizenship === StateRegistrationCitizenship.ChoiceItalian &&
        castedValues.choiceItalian?.actType === ActType.AttoCartaceo &&
        !castedValues.choiceItalian?.paper?.actNumber
      ) {
        errors.push(
          new yup.ValidationError(
            'Campo obbligatorio',
            undefined,
            'newCitizenship.choiceItalian.paper.actNumber'
          )
        );
      }

      if (
        castedValues.reasonCitizenship === StateRegistrationCitizenship.ChoiceItalian &&
        castedValues.choiceItalian?.actType === ActType.AttoCartaceo &&
        !castedValues.choiceItalian?.paper?.actVolume
      ) {
        errors.push(
          new yup.ValidationError(
            'Campo obbligatorio',
            undefined,
            'newCitizenship.choiceItalian.paper.actVolume'
          )
        );
      }

      if (errors?.length === 0) {
        return true;
      }

      return new yup.ValidationError(errors);
    })
    // errors choiceItalian end
    // errors foreignCitizenship start
    .test('newCitizenship.foreignCitizenship.act.year', 'Campo obbligatorio', (values) => {
      const castedValues = values as unknown as CitizenshipFormVM;

      const errors: Array<yup.ValidationError> = [];

      if (
        castedValues.reasonCitizenship === StateRegistrationCitizenship.ForeignCitizenship &&
        castedValues.foreignCitizenship?.actType === ActType.AttoCartaceo &&
        !castedValues.foreignCitizenship?.paper?.actYear
      ) {
        errors.push(
          new yup.ValidationError(
            'Campo obbligatorio',
            undefined,
            'newCitizenship.foreignCitizenship.paper.actYear'
          )
        );
      }

      if (
        castedValues.reasonCitizenship === StateRegistrationCitizenship.ForeignCitizenship &&
        castedValues.foreignCitizenship?.actType === ActType.AttoCartaceo &&
        !castedValues.foreignCitizenship?.paper?.actNumber
      ) {
        errors.push(
          new yup.ValidationError(
            'Campo obbligatorio',
            undefined,
            'newCitizenship.foreignCitizenship.paper.actNumber'
          )
        );
      }

      if (
        castedValues.reasonCitizenship === StateRegistrationCitizenship.ForeignCitizenship &&
        castedValues.foreignCitizenship?.actType === ActType.AttoCartaceo &&
        !castedValues.foreignCitizenship?.paper?.actVolume
      ) {
        errors.push(
          new yup.ValidationError(
            'Campo obbligatorio',
            undefined,
            'newCitizenship.foreignCitizenship.paper.actVolume'
          )
        );
      }

      if (errors?.length === 0) {
        return true;
      }

      return new yup.ValidationError(errors);
    })
    // errors foreignCitizenship end
    // errors foreignNaturalization start
    .test('newCitizenship.foreignNaturalization.act.year', 'Campo obbligatorio', (values) => {
      const castedValues = values as unknown as CitizenshipFormVM;

      const errors: Array<yup.ValidationError> = [];

      if (
        castedValues.reasonCitizenship === StateRegistrationCitizenship.ForeignNaturalization &&
        castedValues.foreignNaturalization?.actType === ActType.AttoCartaceo &&
        !castedValues.foreignNaturalization?.paper?.actYear
      ) {
        errors.push(
          new yup.ValidationError(
            'Campo obbligatorio',
            undefined,
            'newCitizenship.foreignNaturalization.paper.actYear'
          )
        );
      }

      if (
        castedValues.reasonCitizenship === StateRegistrationCitizenship.ForeignNaturalization &&
        castedValues.foreignNaturalization?.actType === ActType.AttoCartaceo &&
        !castedValues.foreignNaturalization?.paper?.actNumber
      ) {
        errors.push(
          new yup.ValidationError(
            'Campo obbligatorio',
            undefined,
            'newCitizenship.foreignNaturalization.paper.actNumber'
          )
        );
      }

      if (
        castedValues.reasonCitizenship === StateRegistrationCitizenship.ForeignNaturalization &&
        castedValues.foreignNaturalization?.actType === ActType.AttoCartaceo &&
        !castedValues.foreignNaturalization?.paper?.actVolume
      ) {
        errors.push(
          new yup.ValidationError(
            'Campo obbligatorio',
            undefined,
            'newCitizenship.foreignNaturalization.paper.actVolume'
          )
        );
      }

      if (errors?.length === 0) {
        return true;
      }

      return new yup.ValidationError(errors);
    })
    // errors foreignNaturalization end
    // errors renunciationCitizenship start
    .test('newCitizenship.renunciationCitizenship.act.year', 'Campo obbligatorio', (values) => {
      const castedValues = values as unknown as CitizenshipFormVM;

      const errors: Array<yup.ValidationError> = [];

      if (
        castedValues.reasonCitizenship === StateRegistrationCitizenship.RenunciationCitizenship &&
        castedValues.renunciationCitizenship?.actType === ActType.AttoCartaceo &&
        !castedValues.renunciationCitizenship?.paper?.actYear
      ) {
        errors.push(
          new yup.ValidationError(
            'Campo obbligatorio',
            undefined,
            'newCitizenship.renunciationCitizenship.paper.actYear'
          )
        );
      }

      if (
        castedValues.reasonCitizenship === StateRegistrationCitizenship.RenunciationCitizenship &&
        castedValues.renunciationCitizenship?.actType === ActType.AttoCartaceo &&
        !castedValues.renunciationCitizenship?.paper?.actNumber
      ) {
        errors.push(
          new yup.ValidationError(
            'Campo obbligatorio',
            undefined,
            'newCitizenship.renunciationCitizenship.paper.actNumber'
          )
        );
      }

      if (
        castedValues.reasonCitizenship === StateRegistrationCitizenship.RenunciationCitizenship &&
        castedValues.renunciationCitizenship?.actType === ActType.AttoCartaceo &&
        !castedValues.renunciationCitizenship?.paper?.actVolume
      ) {
        errors.push(
          new yup.ValidationError(
            'Campo obbligatorio',
            undefined,
            'newCitizenship.renunciationCitizenship.paper.actVolume'
          )
        );
      }

      if (errors?.length === 0) {
        return true;
      }

      return new yup.ValidationError(errors);
    })
    // errors renunciationCitizenship end
    // errors italianCitizenshipDP start
    .test('newCitizenship.italianCitizenshipDP.act.year', 'Campo obbligatorio', (values) => {
      const castedValues = values as unknown as CitizenshipFormVM;

      const errors: Array<yup.ValidationError> = [];

      if (
        castedValues.reasonCitizenship === StateRegistrationCitizenship.ItalianCitizenshipDP &&
        castedValues.italianCitizenshipDP?.actType === ActType.AttoCartaceo &&
        !castedValues.italianCitizenshipDP?.paper?.actYear
      ) {
        errors.push(
          new yup.ValidationError(
            'Campo obbligatorio',
            undefined,
            'newCitizenship.renunciationCitizenship.paper.actYear'
          )
        );
      }

      if (
        castedValues.reasonCitizenship === StateRegistrationCitizenship.ItalianCitizenshipDP &&
        castedValues.italianCitizenshipDP?.actType === ActType.AttoCartaceo &&
        !castedValues.italianCitizenshipDP?.paper?.actNumber
      ) {
        errors.push(
          new yup.ValidationError(
            'Campo obbligatorio',
            undefined,
            'newCitizenship.italianCitizenshipDP.paper.actNumber'
          )
        );
      }

      if (
        castedValues.reasonCitizenship === StateRegistrationCitizenship.ItalianCitizenshipDP &&
        castedValues.italianCitizenshipDP?.actType === ActType.AttoCartaceo &&
        !castedValues.italianCitizenshipDP?.paper?.actVolume
      ) {
        errors.push(
          new yup.ValidationError(
            'Campo obbligatorio',
            undefined,
            'newCitizenship.italianCitizenshipDP.paper.actVolume'
          )
        );
      }

      if (errors?.length === 0) {
        return true;
      }

      return new yup.ValidationError(errors);
    }),
  // errors italianCitizenshipDP end
});
